import React, {useEffect, useState} from 'react';
import {Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis} from 'recharts';
import api from '../../../../api/axios';
import figureConverter from '../../../../tools/figureConverter'; // figureConverter als externe Hilfsfunktion importieren
import Cookies from 'js-cookie';
import useRevenueStoreYear from './RevenueStoreY';

const RevenueChartY = () => {
    const [barColorAG, setBarColorAG] = useState('#8884d8');
    const [barColorVG, setBarColorVG] = useState('#82ca9d');
    const [chartData, setChartData] = useState([]);
    const setTotalRevenue = useRevenueStoreYear((state) => state.setTotalRevenue);

    useEffect(() => {
        const getCSSBackgroundColor = (className) => {
            const element = document.createElement('div');
            element.className = className;
            document.body.appendChild(element);
            const bgColor = window.getComputedStyle(element).backgroundColor;
            document.body.removeChild(element);
            return bgColor;
        };

        const agColor = getCSSBackgroundColor('bg-color-1');
        const vgColor = getCSSBackgroundColor('bg-color-4');

        setBarColorAG(agColor);
        setBarColorVG(vgColor);
    }, []);

    useEffect(() => {
        // Funktion zur Abfrage der produktiven Umsatzdaten
        const fetchRevenueData = async () => {
            try {
                // Access Token aus Cookies abrufen
                const accessToken = Cookies.get('accessToken');

                // Anfrage mit Authorization-Header durchführen
                const response = await api.get('/api/v1/revenue/current/year', {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
                const {snapshots} = response.data;

                // Die Daten in ein passendes Format transformieren
                const transformedData = Object.values(snapshots).map((snapshot) => ({
                    name: `${new Date(snapshot.start).toLocaleString('default', {month: 'short'})}`,
                    revenue: parseFloat(snapshot.revenue),
                    costs: parseFloat(snapshot.costs),
                    yield: parseFloat(snapshot.yield),
                }));

                setChartData(transformedData);
                setTotalRevenue(transformedData); // Speichern der Gesamtsumme im Zustand
            } catch (error) {
                console.error('Fehler beim Abrufen der Umsatzdaten:', error);
            }
        };

        fetchRevenueData();
    }, [setTotalRevenue]);

    const CustomTooltip = ({active, payload}) => {
        if (active && payload && payload.length) {
            const revenueData = payload.find(p => p.dataKey === 'revenue');
            const costsData = payload.find(p => p.dataKey === 'costs');
            const yieldData = payload[0]?.payload?.yield;
            return (
                <div className="custom-tooltip bg-color-3 rounded-lg p-2 opacity-90 border border-color-1">
                    <div style={{display: 'flex', justifyContent: 'space-between', gap: '16px'}}>
                        <span>Monat:</span>
                        <span>{payload[0].payload.name}</span>
                    </div>
                    {revenueData && (
                        <div style={{display: 'flex', justifyContent: 'space-between', gap: '16px'}}>
                            <span>Umsatz:</span>
                            <span>{figureConverter(revenueData.value, false)}</span>
                        </div>
                    )}
                    {costsData && (
                        <div style={{display: 'flex', justifyContent: 'space-between', gap: '16px'}}>
                            <span>Kosten:</span>
                            <span>{figureConverter(costsData.value, false)}</span>
                        </div>
                    )}
                    {yieldData !== undefined && (
                        <div style={{display: 'flex', justifyContent: 'space-between', gap: '16px'}}>
                            <span>Rohertrag:</span>
                            <span>{figureConverter(yieldData, false)}</span>
                        </div>
                    )}
                </div>
            );
        }
        return null;
    };

    const CustomCursor = ({x, y, width, height, fill}) => {
        return (
            <rect
                x={x}
                y={y}
                width={width}
                height={height}
                fill={fill || 'rgba(0, 0, 0, 1)'}
            />
        );
    };

    return (
        <div className='w-full h-full flex items-center justify-center pt-2'>
            <ResponsiveContainer width="100%" height={175}>
                <BarChart margin={0} data={chartData}>
                    <CartesianGrid vertical={false} horizontal={false}/>
                    <XAxis dataKey="name"/>
                    <Tooltip content={<CustomTooltip/>} cursor={<CustomCursor fill="rgba(0, 0, 255, 0.03)"/>}/>
                    <Bar barSize="15" dataKey="revenue" fill={barColorAG}/>
                    <Bar barSize="15" dataKey="costs" fill={barColorVG}/>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default RevenueChartY;